import React, { useContext, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import {
  Box,
  Card,
  FormControl,
  Grid,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import jsPDF from "jspdf";
import "jspdf-autotable";
import NavLogoHlthTek from "../../assets/images/logo/NavLogo_new.png";
import { BrandingContext } from "../../BrandingProvider";
import { displayPatientName } from "../../utils/displayPatientName";
import { usePermissions } from "../login/PermissionContext";
import { toast } from "react-toastify";

const INVOICE_PDF_A5_MM = [148, 210];
const INVOICE_PDF_FOOTER_TEXT =
  "Enhanced Patient Care Powered by Hlthtek Digital Healthcare Platform (visit: https://www.hlthtek.com)";

function getInvoicePdfJsFormat(paperSize) {
  return paperSize === "a5" ? INVOICE_PDF_A5_MM : "a4";
}

function measureInvoicePdfFooter(doc, paperSize) {
  if (paperSize === "a5") {
    return {
      lines: [],
      lineH: 0,
      logoGap: 0,
      logoMaxH: 0,
      padTop: 0,
      padBot: 0,
      bandH: 0,
      narrow: true,
    };
  }
  const pageW = doc.internal.pageSize.getWidth();
  const textMaxW = Math.max(50, pageW - 22);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  const lines = doc.splitTextToSize(INVOICE_PDF_FOOTER_TEXT, textMaxW);
  const lineH = 3.5;
  const logoGap = 1.2;
  const logoMaxH = 7;
  const padTop = 2;
  const padBot = 2;
  const bandH = padTop + lines.length * lineH + logoGap + logoMaxH + padBot;
  return {
    lines,
    lineH,
    logoGap,
    logoMaxH,
    padTop,
    padBot,
    bandH,
    narrow: false,
  };
}

function drawInvoicePdfFooter(doc, footerLogoSrc, layout) {
  const pageHeight = doc.internal.pageSize.height;
  const pageW = doc.internal.pageSize.getWidth();
  const centerX = pageW / 2;
  const { lines, lineH, logoGap, logoMaxH, padTop, bandH, narrow } = layout;
  const footerTop = pageHeight - bandH;
  doc.setFillColor(33, 205, 192);
  doc.rect(0, footerTop, pageW, bandH, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(narrow ? 7 : 10);
  let yText = footerTop + padTop + lineH;
  lines.forEach((line) => {
    doc.text(line, centerX, yText, { align: "center" });
    yText += lineH;
  });
  const logoY = yText - lineH + logoGap;
  try {
    const imgProps = doc.getImageProperties(footerLogoSrc);
    const maxWidth = narrow ? 25 : 40;
    const ratio = imgProps.width / imgProps.height;
    let width = maxWidth;
    let height = width / ratio;
    if (height > logoMaxH) {
      height = logoMaxH;
      width = height * ratio;
    }
    doc.addImage(
      footerLogoSrc,
      "PNG",
      centerX - width / 2,
      logoY,
      width,
      height,
    );
  } catch (e) {
    doc.setFontSize(narrow ? 6 : 9);
    doc.text("Hlthtek", centerX, logoY + (narrow ? 1.5 : 2.5), {
      align: "center",
    });
  }
}

/** Layout constants for refund PDF (A4 vs A5 portrait) — mirrors ViewInvoice. */
function getInvoicePdfPageLayout(doc, paperSize) {
  const PAGE_WIDTH = doc.internal.pageSize.getWidth();
  const PAGE_HEIGHT = doc.internal.pageSize.getHeight();
  const narrow = paperSize === "a5";
  const labelX = narrow ? 6 : 10;
  const valueX = narrow ? 32 : 28;
  const rightX = narrow ? 78 : 120;
  const valueOffset = narrow ? 13 : 16;
  const sideMargin = narrow ? 5 : 10;
  const addrWrap = narrow ? Math.max(28, rightX - valueX - 4) : 70;
  const centerWrap = narrow
    ? Math.max(20, PAGE_WIDTH - sideMargin - (rightX + valueOffset) - 2)
    : 65;
  const headerBandH = narrow ? 13 : 20;
  const infoLineGap = narrow ? 2.5 : 3.5;
  const contentStartY = narrow ? 18 : 30;
  const titleGap = narrow ? 4 : 8;
  return {
    PAGE_WIDTH,
    PAGE_HEIGHT,
    narrow,
    labelX,
    valueX,
    rightX,
    valueOffset,
    addrWrap,
    centerWrap,
    headerBandH,
    infoLineGap,
    contentStartY,
    titleGap,
    tableFont: narrow ? 4.5 : 7,
    tableHeadFont: narrow ? 5 : 8,
    paymentFont: narrow ? 5.5 : 9,
    headerTitleFont: narrow ? 8 : 15,
    sideMargin,
    amountWordsRightEdge: narrow ? rightX - 4 : PAGE_WIDTH - sideMargin - 2,
    tableCellPadding: narrow
      ? { top: 1, bottom: 1, left: 1, right: 1 }
      : { top: 2, bottom: 2, left: 2, right: 2 },
    headerLogoMaxW: narrow ? 30 : 45,
    headerLogoMaxH: narrow ? 9 : 14,
    infoFontSize: narrow ? 5.5 : 7,
    titleFontSize: narrow ? 7 : 10,
  };
}

function alignRefundLineItemsPdfCell(data) {
  if (data.section !== "head" && data.section !== "body") return;
  const i = data.column.index;
  if (i === 0) data.cell.styles.halign = "left";
  else if (i === 1) data.cell.styles.halign = "center";
  else data.cell.styles.halign = "right";
}

/**
 * Prefix + amount in words. A4: 10pt; A5: step font down so text fits — same as ViewInvoice.
 */
function getThanksInWordsLayout(doc, receivedText, amountText, thanksRightEdge, narrow) {
  if (!narrow) {
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const textWidth = doc.getTextWidth(receivedText);
    doc.setFont("helvetica", "bold");
    const maxAmountWidth = Math.max(26, thanksRightEdge - (10 + textWidth));
    const wrappedAmount = doc.splitTextToSize(amountText, maxAmountWidth);
    return {
      thanksFs: 10,
      textWidth,
      wrappedAmount,
      lineHeight: 5,
    };
  }
  let best = {
    thanksFs: 5,
    textWidth: 0,
    wrappedAmount: [amountText],
    lineHeight: 4,
  };
  for (let fs = 10; fs >= 5; fs -= 0.5) {
    doc.setFontSize(fs);
    doc.setFont("helvetica", "normal");
    const tw = doc.getTextWidth(receivedText);
    doc.setFont("helvetica", "bold");
    const maxW = Math.max(14, thanksRightEdge - (10 + tw));
    const wrapped = doc.splitTextToSize(amountText, maxW);
    const lh = fs <= 6 ? 3.8 : fs <= 7.5 ? 4.2 : fs <= 9 ? 4.6 : 5;
    best = {
      thanksFs: fs,
      textWidth: tw,
      wrappedAmount: wrapped,
      lineHeight: lh,
    };
    if (wrapped.length <= 1) {
      break;
    }
  }
  return best;
}

const safeNumber = (val) => {
  const n = Number(String(val ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

/**
 * Normalize refund line pricing when the API omits discount% or charged unit
 * (common on list payloads). Prefers explicit fields, then approved totals,
 * then billed line subtotal/qty, then gross × (1 − discount%).
 */
const resolveRefundLineDisplay = (item) => {
  const approvedQty = safeNumber(item?.approved_quantity ?? item?.quantity ?? 0);
  const grossUnit = safeNumber(
    item?.item_price ?? item?.price ?? item?.rate ?? 0,
  );
  const pctRaw =
    item?.discount_percentage ??
    item?.discount_percent ??
    item?.discount_pct;
  let discountPct = Math.min(100, Math.max(0, safeNumber(pctRaw)));

  const chargedRaw = item?.charged_price;
  let chargedUnit = safeNumber(chargedRaw);
  const chargedMissing =
    chargedRaw == null ||
    String(chargedRaw).trim() === "" ||
    !Number.isFinite(chargedUnit) ||
    chargedUnit <= 0;

  if (chargedMissing) {
    const approvedTotal = safeNumber(item?.approved_amount);
    if (approvedQty > 0 && approvedTotal > 0) {
      chargedUnit = approvedTotal / approvedQty;
    } else if (grossUnit > 0 && discountPct > 0) {
      chargedUnit = grossUnit * (1 - discountPct / 100);
    } else {
      const billedQty = safeNumber(
        item?.quantity ?? item?.billed_quantity ?? item?.invoice_quantity ?? 0,
      );
      const lineSubtotal = safeNumber(item?.subtotal ?? item?.line_total ?? 0);
      if (billedQty > 0 && lineSubtotal > 0) {
        chargedUnit = lineSubtotal / billedQty;
      } else {
        chargedUnit = grossUnit;
      }
    }
  }

  if (
    discountPct === 0 &&
    grossUnit > 0 &&
    chargedUnit > 0 &&
    chargedUnit < grossUnit - 1e-6
  ) {
    discountPct = Math.min(
      100,
      Math.max(0, (1 - chargedUnit / grossUnit) * 100),
    );
  }

  const refundAmount = safeNumber(
    item?.approved_amount ?? approvedQty * chargedUnit,
  );

  const itemName = (item?.item_name || item?.service_name || item?.name || "").toLowerCase().trim();
  const isDoctorConsultation = itemName === "doctor consultation";
  const resolvedGrossUnit = grossUnit > 0 ? grossUnit : (isDoctorConsultation ? chargedUnit : grossUnit);

  return {
    item_name: item?.item_name || item?.service_name || item?.name || "—",
    quantity: approvedQty,
    original_item_price: resolvedGrossUnit,
    discount_percentage: discountPct,
    charged_price: chargedUnit,
    refund_amount: refundAmount,
  };
};

const formatMoney = (val) => safeNumber(val).toFixed(2);
const pickFirstNonEmpty = (...values) => {
  for (const value of values) {
    const s = String(value ?? "").trim();
    if (!s) continue;
    if (/^n\/?a$/i.test(s) || s.toLowerCase() === "null" || s.toLowerCase() === "undefined") {
      continue;
    }
    return s;
  }
  return "";
};

const numberToWords = (num) => {
  const a = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const b = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];
  const inWords = (n) => {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? ` ${a[n % 10]}` : "");
    if (n < 1000)
      return (
        a[Math.floor(n / 100)] +
        " Hundred" +
        (n % 100 ? ` ${inWords(n % 100)}` : "")
      );
    if (n < 100000)
      return (
        inWords(Math.floor(n / 1000)) +
        " Thousand" +
        (n % 1000 ? ` ${inWords(n % 1000)}` : "")
      );
    if (n < 10000000)
      return (
        inWords(Math.floor(n / 100000)) +
        " Lakh" +
        (n % 100000 ? ` ${inWords(n % 100000)}` : "")
      );
    if (n < 1000000000)
      return (
        inWords(Math.floor(n / 10000000)) +
        " Crore" +
        (n % 10000000 ? ` ${inWords(n % 10000000)}` : "")
      );
    return "";
  };
  return inWords(num);
};

const formatDatePdf = (raw) => {
  if (!raw || raw === "N/A") return "N/A";
  try {
    const d = raw instanceof Date ? raw : new Date(raw);
    if (Number.isNaN(d.getTime())) return String(raw);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  } catch (e) {
    return String(raw);
  }
};

const buildFacilityAddressPdf = (info) => {
  const parts = [
    info?.facility_address_line_1,
    info?.facility_address_line_2,
    info?.facility_address_line_3,
    info?.city,
    info?.state,
    info?.pincode ? `PIN: ${info.pincode}` : null,
  ];
  return parts.filter((x) => x && String(x).trim() !== "").join(", ");
};

const RefundReceipt = () => {
  const location = useLocation();
  const history = useHistory();
  const brandingData = useContext(BrandingContext);
  const { userInfo } = usePermissions();
  const [paperSize, setPaperSize] = useState("a4");
  const [printMenuAnchorEl, setPrintMenuAnchorEl] = useState(null);
  const [downloadMenuAnchorEl, setDownloadMenuAnchorEl] = useState(null);
  const data = location?.state?.data || null;

  const collateralImageItem = brandingData?.find(
    (item) => item.name === "collateral_image",
  );
  const collateralImage = collateralImageItem?.content || null;

  const refundNumber = data?.refund_number || data?.receipt_no || "N/A";
  const invoiceNumber = data?.invoice_number || data?.invoice_no || "N/A";
  const patientName = displayPatientName(data?.patient_name || data?.patientName || "N/A");
  const patientId = data?.patient_id ?? data?.patient_record_id ?? data?.patientId ?? "N/A";
  const refundDate = formatDatePdf(
    data?.approved_at || data?.payment_date || data?.created_at,
  );
  const settlementStatus = String(data?.settlement_status || "UNSETTLED").toUpperCase();
  const refundAmount = safeNumber(data?.refund_amount ?? data?.amount_paid ?? data?.payment_amount ?? 0);

  const firstPaymentRow = useMemo(() => {
    const d = data;
    if (!d) return null;
    if (Array.isArray(d.payments) && d.payments.length > 0) return d.payments[0];
    if (Array.isArray(d.payment_details) && d.payment_details.length > 0)
      return d.payment_details[0];
    return d.payment && typeof d.payment === "object" ? d.payment : null;
  }, [data]);

  const paymentMode = useMemo(() => {
    const d = data;
    if (!d) return "N/A";
    const resolved = pickFirstNonEmpty(
      d.mode_of_payment,
      d.payment_mode,
      d.payment_method,
      d.pay_mode,
      d.paymentType,
      d.payment_type,
      d.mode,
      firstPaymentRow?.mode_of_payment,
      firstPaymentRow?.payment_mode,
      firstPaymentRow?.payment_method,
      firstPaymentRow?.pay_mode,
      firstPaymentRow?.paymentType,
      firstPaymentRow?.payment_type,
      firstPaymentRow?.mode,
      firstPaymentRow?.method,
    );
    return resolved || "N/A";
  }, [data, firstPaymentRow]);

  const refNo = useMemo(() => {
    const d = data;
    if (!d) return "N/A";
    const resolved = pickFirstNonEmpty(
      d.ref_no,
      d.transaction_ref_no,
      d.transaction_reference,
      d.reference_no,
      d.reference_number,
      d.payment_reference,
      d.payment_ref,
      d.txn_id,
      d.transaction_id,
      d.utr_no,
      d.utr,
      d.rrn,
      firstPaymentRow?.transaction_ref_no,
      firstPaymentRow?.ref_no,
      firstPaymentRow?.transaction_reference,
      firstPaymentRow?.reference_no,
      firstPaymentRow?.reference_number,
      firstPaymentRow?.payment_reference,
      firstPaymentRow?.payment_ref,
      firstPaymentRow?.txn_id,
      firstPaymentRow?.transaction_id,
      firstPaymentRow?.utr_no,
      firstPaymentRow?.utr,
      firstPaymentRow?.rrn,
    );
    return resolved || "N/A";
  }, [data, firstPaymentRow]);

  const refundItems = useMemo(() => {
    const rawItems =
      data?.itemsList || data?.items || data?.invoice_items || data?.invoiceItems || [];
    if (!Array.isArray(rawItems)) return [];
    return rawItems
      .map((item) => resolveRefundLineDisplay(item))
      .filter((row) => row.quantity > 0 && row.refund_amount > 0);
  }, [data]);

  const linesTotal = useMemo(
    () => refundItems.reduce((sum, row) => sum + safeNumber(row.refund_amount), 0),
    [refundItems],
  );

  const centerNameDisplay = useMemo(
    () =>
      data?.center ||
      data?.facility_name ||
      data?.clinic_name ||
      userInfo?.facility_name ||
      (typeof window !== "undefined" && sessionStorage.getItem("facility_display_name")) ||
      "Clinic",
    [data, userInfo],
  );

  const centerAddressDisplay = useMemo(
    () =>
      data?.center_address ||
      data?.facility_address ||
      data?.clinic_address ||
      buildFacilityAddressPdf(userInfo) ||
      "N/A",
    [data, userInfo],
  );
  const signatoryUserName = useMemo(
    () => String(userInfo?.name || sessionStorage.getItem("username") || "N/A").trim() || "N/A",
    [userInfo],
  );

  const billAmountUi = linesTotal > 0.01 ? linesTotal : refundAmount;
  const amountPaidUi = refundAmount;
  const dueAmountUi = Math.max(0, safeNumber(billAmountUi) - safeNumber(amountPaidUi));
  const amountPaidInWords = `${numberToWords(Math.floor(Number(amountPaidUi || 0)))} Rupees Only`;

  if (!data) return null;

  const drawPdf = (download = true, withLetterhead = true) => {
    const narrow = paperSize === "a5";
    const rowsForPdf =
      refundItems.length > 0
        ? refundItems
        : [
            {
              item_name: "—",
              quantity: 0,
              original_item_price: 0,
              discount_percentage: 0,
              charged_price: 0,
              refund_amount: 0,
            },
          ];

    const doc = new jsPDF("p", "mm", getInvoicePdfJsFormat(paperSize));
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const m = narrow ? 8 : 14;
    const innerW = pageW - 2 * m;
    const headerBandH = narrow ? 13 : 20;
    const headerTitleFont = narrow ? 8 : 15;
    const headerLogoMaxW = narrow ? 30 : 45;
    const headerLogoMaxH = narrow ? 9 : 14;
    const headerLogoDataUrl = collateralImage
      ? `data:image/png;base64,${collateralImage}`
      : null;
    const footerLayout = measureInvoicePdfFooter(doc, paperSize);
    const bottomMargin = narrow
      ? 8
      : withLetterhead
        ? Math.max(25, footerLayout.bandH + 8)
        : Math.max(22, footerLayout.bandH + 5);

    function drawHeader() {
      doc.setFillColor(33, 205, 192);
      doc.rect(0, 0, pageW, headerBandH, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(headerTitleFont);
      doc.text(
        sessionStorage.getItem("facility_display_name") ||
          data?.facility_name ||
          centerNameDisplay ||
          "Super Galaxy Health",
        narrow ? 6 : 10,
        narrow ? 8 : 12,
      );
      doc.setTextColor(0);
      try {
        if (headerLogoDataUrl) {
          const imgProps = doc.getImageProperties(headerLogoDataUrl);
          const ratio = imgProps.width / imgProps.height;
          let width = headerLogoMaxW;
          let height = width / ratio;
          if (height > headerLogoMaxH) {
            height = headerLogoMaxH;
            width = height * ratio;
          }
          doc.addImage(
            headerLogoDataUrl,
            "PNG",
            pageW - m - width,
            narrow ? 2 : 3,
            width,
            height,
          );
        }
      } catch (e) {}
    }

    function drawFooterTemplate() {
      if (narrow) return;
      drawInvoicePdfFooter(doc, NavLogoHlthTek, footerLayout);
    }

    doc.autoTableSetDefaults({
      margin: {
        bottom: bottomMargin,
        ...(withLetterhead ? {} : { top: narrow ? 20 : 30 }),
      },
      didDrawPage: withLetterhead
        ? function () {
            drawHeader();
            drawFooterTemplate();
          }
        : function () {},
    });

    if (withLetterhead) {
      drawHeader();
    }
    let y = withLetterhead ? headerBandH + 3 : narrow ? 20 : 30;
    y += narrow ? 2 : 3;

    const leftX = m;
    const rightX = m + innerW * 0.63;
    const rightWidth = pageW - m - rightX;
    const lineH = narrow ? 4.8 : 5.5;

    const drawLabelValue = (x, yPos, label, value, maxW, valueColor = [0, 0, 0]) => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(narrow ? 8 : 9);
      doc.setTextColor(0, 0, 0);
      const labelStr = `${label}: `;
      doc.text(labelStr, x, yPos);
      const labelW = doc.getTextWidth(labelStr);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...valueColor);
      const lines = doc.splitTextToSize(String(value ?? ""), Math.max(20, maxW - labelW));
      doc.text(lines, x + labelW, yPos);
      doc.setTextColor(0, 0, 0);
      return yPos + Math.max(lineH, lines.length * lineH);
    };

    let leftY = y;
    let rightY = y;

    leftY = drawLabelValue(leftX, leftY, "Patient Name", String(patientName).toUpperCase(), innerW * 0.58);
    leftY = drawLabelValue(leftX, leftY, "Pat ID", String(patientId), innerW * 0.58);
    leftY = drawLabelValue(leftX, leftY, "Refund Invoice No", String(refundNumber), innerW * 0.58);
    leftY = drawLabelValue(leftX, leftY, "Original Invoice No", String(invoiceNumber), innerW * 0.58);

    rightY = drawLabelValue(
      rightX,
      rightY,
      "Settlement",
      String(settlementStatus),
      rightWidth,
      settlementStatus === "SETTLED" ? [33, 205, 192] : [154, 52, 18],
    );
    rightY = drawLabelValue(rightX, rightY, "Refund Date", refundDate, rightWidth);
    rightY = drawLabelValue(rightX, rightY, "Center", centerNameDisplay, rightWidth);
    rightY = drawLabelValue(rightX, rightY, "Address", centerAddressDisplay, rightWidth);

    y = Math.max(leftY, rightY) + 1;
    doc.line(m, y, pageW - m, y);
    y += narrow ? 5 : 6;

    y += 2;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(narrow ? 9 : 10);
    doc.text("Refund Receipt", m, y);
    y += narrow ? 4 : 5;

    const tableBody = rowsForPdf.map((item) => [
      item.item_name || "—",
      String(item.quantity ?? 0),
      `Rs ${formatMoney(item.original_item_price)}`,
      `${formatMoney(item.discount_percentage)}%`,
      `Rs ${formatMoney(item.charged_price)}`,
      `Rs ${formatMoney(item.refund_amount)}`,
    ]);

    doc.autoTable({
      startY: y,
      head: [[
        "Service Description",
        "Qty",
        "Item Price (Rs.)",
        "Discount %",
        "Charged Price (Rs.)",
        "Refund Amount (Rs.)",
      ]],
      body: tableBody,
      theme: "grid",
      styles: {
        font: "helvetica",
        fontSize: narrow ? 7 : 8,
        cellPadding: narrow ? 1.5 : 2,
        lineColor: [200, 200, 200],
        lineWidth: 0.1,
      },
      columnStyles: {
        1: { halign: "center" },
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
      },
      headStyles: {
        fillColor: [232, 248, 245],
        textColor: 20,
        fontStyle: "bold",
        fontSize: narrow ? 7.5 : 8.5,
      },
      didParseCell: (hookData) => {
        if (hookData.column.index === 1) {
          hookData.cell.styles.halign = "center";
        }
        if (
          hookData.column.index === 2 ||
          hookData.column.index === 3 ||
          hookData.column.index === 4 ||
          hookData.column.index === 5
        ) {
          hookData.cell.styles.halign = "right";
        }
      },
      margin: { left: m, right: m },
    });
    y = doc.lastAutoTable.finalY + 6;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(narrow ? 9 : 10);
    doc.text("Payment Details", m, y);
    y += narrow ? 4 : 5;

    doc.autoTable({
      startY: y,
      head: [["Mode", "Ref.No", "Amount"]],
      body: [[`${paymentMode}`, `${refNo}`, `Rs ${formatMoney(refundAmount)}`]],
      theme: "grid",
      styles: {
        font: "helvetica",
        fontSize: narrow ? 7 : 8,
        cellPadding: narrow ? 1.5 : 2,
        lineColor: [200, 200, 200],
        lineWidth: 0.1,
      },
      columnStyles: {
        2: { halign: "right" },
      },
      headStyles: {
        fillColor: [232, 248, 245],
        textColor: 20,
        fontStyle: "bold",
        fontSize: narrow ? 7.5 : 8.5,
      },
      didParseCell: (hookData) => {
        if (hookData.column.index === 2) {
          hookData.cell.styles.halign = "right";
        }
      },
      margin: { left: m, right: m },
    });
    y = doc.lastAutoTable.finalY + 8;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(narrow ? 8.5 : 9.5);
    doc.setTextColor(0, 0, 0);
    doc.text(`Refundable Amount: Rs ${formatMoney(billAmountUi)}`, pageW - m, y, { align: "right" });
    y += narrow ? 4.5 : 5;
    doc.setFont("helvetica", "bold");
    doc.setTextColor(14, 63, 102);
    doc.text(`Refunded Amount: Rs ${formatMoney(amountPaidUi)}`, pageW - m, y, { align: "right" });
    doc.setTextColor(0, 0, 0);
    y += narrow ? 4.5 : 5;
    doc.setFont("helvetica", "normal");
    doc.text(`Due Amount: Rs ${formatMoney(dueAmountUi)}`, pageW - m, y, { align: "right" });
    const wordsY = y - (narrow ? 4.5 : 5);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(narrow ? 8 : 9);
    const wordsPrefix = "Refunded: ";
    const wordsText = `${wordsPrefix}${amountPaidInWords}`;
    const wordsMaxW = Math.max(40, pageW - (m * 2) - (narrow ? 52 : 70));
    const wrappedWords = doc.splitTextToSize(wordsText, wordsMaxW);
    doc.text(wrappedWords, m, wordsY);
    const wordsLineH = narrow ? 4 : 5;
    const wordsBottomY = wordsY + wrappedWords.length * wordsLineH;
    y = Math.max(y + (narrow ? 8 : 10), wordsBottomY + (narrow ? 2 : 3));

    const signatoryX = pageW - m;
    const signatoryY = y;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(narrow ? 9 : 10);
    doc.setTextColor(0, 0, 0);
    doc.text("Patient's Signature", m, signatoryY);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(narrow ? 9 : 10);
    doc.setTextColor(0, 0, 0);
    doc.text("Authorised Signatory", signatoryX, signatoryY, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(narrow ? 8.5 : 9.5);
    doc.text(signatoryUserName, signatoryX, signatoryY + (narrow ? 9.6 : 12), {
      align: "right",
    });
    y = signatoryY + (narrow ? 14 : 18);

    if (y > pageH - 18) {
      y = pageH - 16;
    }
    doc.setFontSize(narrow ? 7 : 8);
    doc.setTextColor(115, 115, 115);
    doc.setFont("helvetica", "italic");
    doc.text("Note: This is a digitally generated refund receipt.", m, y);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);

    if (download) {
      doc.save(
        `Refund_Receipt-${String(invoiceNumber).replace(/[^a-zA-Z0-9-_]/g, "_")}.pdf`,
      );
    } else {
      const blobUrl = doc.output("bloburl");
      window.open(`${blobUrl}#zoom=page-fit`, "_blank");
    }
  };

  return (
    <Card style={{ marginBottom: "16px" }}>
      <Card
        style={{
          width: "95%",
          height: "100%",
          padding: "16px",
          marginLeft: "40px",
          marginTop: "1%",
        }}
      >
        <div id="refund-receipt-template">
          <Grid
            container
            alignItems="center"
            sx={{ px: { xs: 2, sm: "40px" }, pt: 1.5, pb: 0.5 }}
          >
            <Grid item xs={6}>
              {collateralImage ? (
                <img
                  src={`data:image/png;base64,${collateralImage}`}
                  height={44}
                  alt=""
                  style={{ objectFit: "contain", maxWidth: "100%" }}
                />
              ) : null}
            </Grid>
            <Grid item xs={6} sx={{ textAlign: "right" }}>
              <img
                src={NavLogoHlthTek}
                height={40}
                alt="HLTHTek"
                style={{ objectFit: "contain" }}
              />
            </Grid>
          </Grid>

          <Grid
            container
            style={{
              padding: "0px 40px 16px 40px",
              fontSize: "18px",
              lineHeight: "25px",
            }}
          >
            <Grid item xs={12} md={7}>
              <div
                style={{
                  display: "grid",
                  rowGap: "2px",
                  lineHeight: 1.35,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: 4,
                    lineHeight: 1.2,
                  }}
                >
                  <Box
                    sx={{
                      px: 1,
                      py: 0.25,
                      borderRadius: "4px",
                      fontSize: "12px",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      color: "#065f46",
                      backgroundColor: "#d1fae5",
                    }}
                  >
                    Paid
                  </Box>
                </div>
                <div>
                  <b>Patient Name:</b> {String(patientName).toUpperCase()}
                </div>
                <div>
                  <b>Pat ID:</b> {String(patientId)}
                </div>
                <div>
                  <b>Refund Invoice No:</b> {String(refundNumber)}
                </div>
                <div>
                  <b>Original Invoice No:</b> {String(invoiceNumber)}
                </div>
              </div>
            </Grid>
            <Grid
              item
              xs={12}
              md={5}
              sx={{
                pl: { xs: 0, md: "80px" },
                pt: { xs: 2, md: 0 },
              }}
            >
              <div style={{ display: "grid", rowGap: "4px" }}>
                <div>
                  <b>Settlement:</b>{" "}
                  <span
                    style={{
                      color: settlementStatus === "SETTLED" ? "#21cdc0" : "#9a3412",
                      fontWeight: 700,
                    }}
                  >
                    {settlementStatus}
                  </span>
                </div>
                <div>
                  <b>Refund Date:</b> {refundDate}
                </div>
                <div>
                  <b>Center:</b> {centerNameDisplay}
                </div>
                <div style={{ maxWidth: "420px", wordBreak: "break-word" }}>
                  <b>Address:</b> {centerAddressDisplay}
                </div>
              </div>
            </Grid>
          </Grid>

          <Card style={{ height: "auto" }}>
            <Grid
              container
              style={{
                backgroundColor: "#C7F4EF",
                padding: "16px 20px",
                alignItems: "center",
                borderRadius: "8px 8px 0 0",
              }}
            >
              <Grid item xs={6}>
                  <Typography variant="h6" fontWeight="bold">
                    Refund Receipt
                  </Typography>
              </Grid>
              <Grid item xs={6} style={{ textAlign: "right" }}>
                {collateralImage ? (
                  <img
                    src={`data:image/png;base64,${collateralImage}`}
                    height="40"
                    alt=""
                  />
                ) : null}
              </Grid>
            </Grid>

            <Grid
              container
              style={{
                padding: "20px",
                display: "flex",
                justifyContent: "left",
                width: "100%",
              }}
            >
              <Grid item xs={12}>
                <div style={{ width: "100%" }}>
                  <Grid
                    container
                    style={{
                      fontWeight: "bold",
                      padding: "10px 0",
                      borderBottom: "1px solid #ddd",
                    }}
                  >
                    <Grid item xs={4}>
                      Service Description
                    </Grid>
                    <Grid item xs={1} style={{ textAlign: "center" }}>
                      Qty
                    </Grid>
                    <Grid item xs={2} style={{ textAlign: "right" }}>
                      Item Price (Rs.)
                    </Grid>
                    <Grid item xs={1} style={{ textAlign: "right" }}>
                      Discount %
                    </Grid>
                    <Grid item xs={2} style={{ textAlign: "right" }}>
                      Charged Price (Rs.)
                    </Grid>
                    <Grid item xs={2} style={{ textAlign: "right" }}>
                      Refund Amount (Rs.)
                    </Grid>
                  </Grid>
                  {refundItems.length === 0 ? (
                    <Grid
                      container
                      style={{
                        borderBottom: "1px solid #eee",
                        padding: "10px 0",
                        color: "#666",
                        fontStyle: "italic",
                      }}
                    >
                      <Grid item xs={12}>
                        No line items
                      </Grid>
                    </Grid>
                  ) : (
                    refundItems.map((item, index) => (
                      <Grid
                        container
                        key={index}
                        style={{
                          borderBottom: "1px solid #eee",
                          padding: "10px 0",
                        }}
                      >
                        <Grid item xs={4}>
                          {item.item_name}
                        </Grid>
                        <Grid item xs={1} style={{ textAlign: "center" }}>
                          {item.quantity}
                        </Grid>
                        <Grid item xs={2} style={{ textAlign: "right" }}>
                          Rs {formatMoney(item.original_item_price)}
                        </Grid>
                        <Grid item xs={1} style={{ textAlign: "right" }}>
                          {formatMoney(item.discount_percentage)}%
                        </Grid>
                        <Grid item xs={2} style={{ textAlign: "right" }}>
                          Rs {formatMoney(item.charged_price)}
                        </Grid>
                        <Grid item xs={2} style={{ textAlign: "right" }}>
                          Rs {formatMoney(item.refund_amount)}
                        </Grid>
                      </Grid>
                    ))
                  )}
                </div>
              </Grid>
            </Grid>

            <Grid
              container
              style={{
                padding: "20px",
                marginTop: "8px",
                alignItems: "flex-start",
              }}
            >
              <Grid item xs={12} md={7}>
                <Grid
                  container
                  style={{
                    fontWeight: "bold",
                    borderBottom: "1px solid #ccc",
                    paddingBottom: "6px",
                  }}
                >
                  <Grid item xs={4}>
                    Mode
                  </Grid>
                  <Grid item xs={4}>
                    Ref.No
                  </Grid>
                  <Grid item xs={4} style={{ textAlign: "right" }}>
                    Amount
                  </Grid>
                </Grid>
                <Grid container style={{ paddingTop: "6px" }}>
                  <Grid item xs={4}>
                    {paymentMode}
                  </Grid>
                  <Grid item xs={4}>
                    {refNo}
                  </Grid>
                  <Grid item xs={4} style={{ textAlign: "right", fontWeight: 600 }}>
                    Rs {formatMoney(refundAmount)}
                  </Grid>
                </Grid>
                <Typography
                  variant="body1"
                  style={{
                    marginTop: "36px",
                    maxWidth: "95%",
                    wordBreak: "break-word",
                    lineHeight: 1.4,
                  }}
                >
                  Refunded: <b>{amountPaidInWords}</b>
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  style={{ marginTop: "36px" }}
                >
                  Patient&apos;s Signature
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                md={5}
                style={{ display: "flex", justifyContent: "flex-end" }}
              >
                <div style={{ textAlign: "right" }}>
                  <Typography variant="body2">
                    Refundable Amount: Rs {formatMoney(billAmountUi)}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    style={{ marginTop: "8px", color: "#0e3f66" }}
                  >
                    Refunded Amount: Rs {formatMoney(amountPaidUi)}
                  </Typography>
                  <Typography variant="body2" style={{ marginTop: "8px" }}>
                    Due Amount: Rs {formatMoney(dueAmountUi)}
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    style={{ marginTop: "36px" }}
                  >
                    Authorised Signatory
                  </Typography>
                  <Typography variant="body1" style={{ marginTop: "16px" }}>
                    {signatoryUserName}
                  </Typography>
                </div>
              </Grid>
            </Grid>

            <Grid container style={{ padding: "10px 20px 24px 20px" }}>
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontStyle: "italic" }}
                >
                  Note: This is a digitally generated refund receipt.
                </Typography>
              </Grid>
            </Grid>
          </Card>
        </div>

        <Grid container justifyContent="center" style={{ marginTop: "24px" }}>
          <Grid item xs={12} sm={8} md={4}>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel id="refund-paper-size-label">Paper size (PDF and print)</InputLabel>
              <Select
                labelId="refund-paper-size-label"
                id="refund-paper-size"
                label="Paper size (PDF and print)"
                value={paperSize}
                onChange={(e) => setPaperSize(e.target.value)}
              >
                <MenuItem value="a4">A4 (210 × 297 mm)</MenuItem>
                <MenuItem value="a5">A5 (148 × 210 mm)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Grid
          container
          justifyContent="center"
          alignItems="center"
          spacing={4}
          style={{ marginTop: "16px", marginBottom: "24px" }}
        >
          <Grid item xs={12} md={4} style={{ textAlign: "center" }}>
            <button
              type="button"
              onClick={(e) => setDownloadMenuAnchorEl(e.currentTarget)}
              style={{
                width: "100%",
                padding: "18px 0",
                backgroundColor: "#21cdc0",
                color: "#fff",
                borderRadius: "10px",
                fontSize: "22px",
                fontWeight: 600,
                border: "none",
                cursor: "pointer",
              }}
            >
              DOWNLOAD PDF
            </button>
            <Menu
              anchorEl={downloadMenuAnchorEl}
              open={Boolean(downloadMenuAnchorEl)}
              onClose={() => setDownloadMenuAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              transformOrigin={{ vertical: "top", horizontal: "center" }}
              PaperProps={{
                style: {
                  width: downloadMenuAnchorEl
                    ? downloadMenuAnchorEl.offsetWidth
                    : undefined,
                },
              }}
            >
              <MenuItem
                onClick={() => {
                  drawPdf(true, true);
                  setDownloadMenuAnchorEl(null);
                }}
              >
                Refund Receipt with Letterhead
              </MenuItem>
              <MenuItem
                onClick={() => {
                  drawPdf(true, false);
                  setDownloadMenuAnchorEl(null);
                }}
              >
                Refund Receipt without Letterhead
              </MenuItem>
            </Menu>
          </Grid>
          <Grid item xs={12} md={4} style={{ textAlign: "center" }}>
            <button
              type="button"
              onClick={(e) => setPrintMenuAnchorEl(e.currentTarget)}
              style={{
                width: "100%",
                padding: "18px 0",
                backgroundColor: "#21cdc0",
                color: "#fff",
                borderRadius: "10px",
                fontSize: "22px",
                fontWeight: 600,
                border: "none",
                cursor: "pointer",
              }}
            >
              PRINT
            </button>
            <Menu
              anchorEl={printMenuAnchorEl}
              open={Boolean(printMenuAnchorEl)}
              onClose={() => setPrintMenuAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              transformOrigin={{ vertical: "top", horizontal: "center" }}
              PaperProps={{
                style: {
                  width: printMenuAnchorEl
                    ? printMenuAnchorEl.offsetWidth
                    : undefined,
                },
              }}
            >
              <MenuItem
                onClick={() => {
                  drawPdf(false, true);
                  setPrintMenuAnchorEl(null);
                }}
              >
                Refund Receipt with Letterhead
              </MenuItem>
              <MenuItem
                onClick={() => {
                  drawPdf(false, false);
                  setPrintMenuAnchorEl(null);
                }}
              >
                Refund Receipt without Letterhead
              </MenuItem>
            </Menu>
          </Grid>
          <Grid item xs={12} md={4} style={{ textAlign: "center" }}>
            <button
              type="button"
              onClick={() => history.push("/doctor/refund")}
              style={{
                width: "100%",
                padding: "18px 0",
                backgroundColor: "#21cdc0",
                color: "#fff",
                borderRadius: "10px",
                fontSize: "22px",
                fontWeight: 600,
                border: "none",
                cursor: "pointer",
              }}
            >
              GO TO HOME
            </button>
          </Grid>
        </Grid>
      </Card>
    </Card>
  );
};

export default RefundReceipt;
